import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';
import { Button, cn } from './primitives';
import { t } from '../../i18n';

/**
 * The confirmation every destructive action goes through.
 *
 * Radix Dialog rather than a hand-rolled modal, because it gets the parts that
 * are easy to miss right: focus moves in on open and back to the trigger on
 * close, focus is trapped while open, Escape closes, and the rest of the page
 * is `aria-hidden` so a screen reader does not wander out of the dialog.
 *
 * Two deliberate choices:
 *  - **The confirm button is not focused on open.** Enter should not delete
 *    something because someone was still typing.
 *  - **`confirmPhrase` for the irreversible ones.** Typing the record's name
 *    is friction, and friction is the point when the action cannot be undone.
 */

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Say what will happen, in the concrete. "Delete 12 members" beats "Are you sure?". */
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'default';
  loading?: boolean;
  /** When set, the confirm button stays disabled until this exact text is typed. */
  confirmPhrase?: string;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  tone = 'danger',
  loading = false,
  confirmPhrase,
  onConfirm,
}: ConfirmDialogProps): React.JSX.Element {
  const [typed, setTyped] = React.useState('');
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const blocked = Boolean(confirmPhrase) && typed.trim() !== confirmPhrase;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink-1/40 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          // Focus lands on Cancel, not Confirm — see the note above.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            cancelRef.current?.focus();
          }}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-sk-2xl border border-outline bg-surface shadow-sk-lg focus-visible:outline-none"
        >
          <div className="flex items-start gap-3 px-5 pb-3 pt-5">
            {tone === 'danger' ? (
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-danger-soft">
                <AlertTriangle className="size-4 text-danger-ink" aria-hidden />
              </span>
            ) : null}
            <div className="min-w-0 flex-1">
              <Dialog.Title className="font-display text-base font-bold text-ink-1">
                {title}
              </Dialog.Title>
              <Dialog.Description asChild>
                <div className="mt-1 text-[13px] leading-relaxed text-ink-2">{description}</div>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label={t('Close')}
                className="rounded-sk-sm p-1 text-ink-3 transition-colors hover:bg-surface-variant hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo/60"
              >
                <X className="size-4" aria-hidden />
              </button>
            </Dialog.Close>
          </div>

          {confirmPhrase ? (
            <div className="px-5 pb-1">
              <label htmlFor="confirm-phrase" className="text-[13px] font-semibold text-ink-1">
                {t('Type')}{' '}<span className="font-mono font-bold">{confirmPhrase}</span>{' '}{t('to confirm')}
              </label>
              <input
                id="confirm-phrase"
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                autoComplete="off"
                className="mt-1.5 h-10 w-full rounded-sk-md border border-outline-field bg-surface px-3 text-sm text-ink-1 focus-visible:border-indigo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo/25"
              />
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 px-5 py-4">
            <Dialog.Close asChild>
              <Button ref={cancelRef} variant="secondary" disabled={loading}>
                {cancelLabel}
              </Button>
            </Dialog.Close>
            <Button
              variant={tone === 'danger' ? 'danger' : 'primary'}
              loading={loading}
              disabled={blocked}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Wires up the open/close state and the pending record for a confirm dialog.
 *
 * Saves every list page repeating the same three pieces of state, and keeps
 * the target alive through the closing animation so the dialog does not blank
 * out mid-fade.
 */
export function useConfirm<TTarget>(): {
  target: TTarget | null;
  open: boolean;
  ask: (target: TTarget) => void;
  dismiss: () => void;
  onOpenChange: (open: boolean) => void;
} {
  const [target, setTarget] = React.useState<TTarget | null>(null);
  const [open, setOpen] = React.useState(false);

  return {
    target,
    open,
    ask: (next) => {
      setTarget(next);
      setOpen(true);
    },
    dismiss: () => setOpen(false),
    onOpenChange: (next) => {
      setOpen(next);
      if (!next) setTimeout(() => setTarget(null), 150);
    },
  };
}

/** A shared shell for the other dialogs in this folder. */
export function DialogShell({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink-1/40 backdrop-blur-[1px]" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-3rem)] w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-sk-2xl border border-outline bg-surface shadow-sk-lg focus-visible:outline-none',
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-outline-soft px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="font-display text-base font-bold text-ink-1">
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description asChild>
                  <div className="mt-0.5 text-[13px] text-ink-2">{description}</div>
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label={t('Close')}
                className="rounded-sk-sm p-1 text-ink-3 transition-colors hover:bg-surface-variant hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo/60"
              >
                <X className="size-4" aria-hidden />
              </button>
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-outline-soft px-5 py-4">
              {footer}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
